import { useCallback, useEffect, useState } from 'react';

import { getCardItemTags } from '../services/itemTags';
import { normalizeId } from '../utils/ids';
import { getSessionKey, onSessionChange, useSessionUserId } from '../utils/session';

/**
 * WHERE A CARD STANDS on "Use items as tags" in one collection, for an item
 * of it (docs/plans/tags.md §5.2 "Item ⋯ and read view"; build step T4d):
 * `GET /api/collections/{id}/cards/{cardId}/item-tags` → `{ mode,
 * applyWhereReferenced, fromCardId, ownMode, count, canManage, pass? }`.
 *
 * The item's ⋯ offers **Use as a tag** / **Stop using as a tag** only to a
 * manager of the ITEM's collection, and only on a card in Chosen items mode
 * (Every item has no per-item exclusion in v1 — ruled d39). The collection a
 * surface holds is not always the item's (a parent's list shows its
 * sub-collections' items), so this asks the API, which reads the setting the
 * way it will judge the act — from the item's own collection, the nearest card
 * up the chain deciding — and says whether the caller may act: `canManage`,
 * manage on the collection AND the collection not archived, exactly what the
 * item-tag writes ask (the API's T4d follow-up). ⚑ It is read as given, and
 * only `true` counts: before the follow-up the web inferred "manages" from
 * the presence of `pass`, which every manager still gets — archived or not —
 * so it said yes on an archived collection whose writes all refuse.
 *
 * Every row of a list asks about one or two cards, so it is fetched ONCE per
 * collection and card and shared (the useCollectionTags shape): session-
 * stamped, cleared on a change of account, stale after a minute, a failed
 * read not remembered, and invalidated by the card's settings window
 * (`invalidateItemTagSetting`) the moment a manager changes it.
 *
 * A surface asks only where the person might manage (`enabled`): a reader
 * never makes the request, and never sees the actions.
 *
 * `useItemTagSetting(collectionId, cardId, { enabled })` → `{ mode,
 * fromCardId, canManage, count, pass, loaded, error, refresh }`.
 */

const STALE_MS = 60 * 1000;
// `${collectionId}:${cardId}` -> { at, owner, promise, value, loaded, pending }
const cache = new Map();
// key -> Set<(value) => void>
const listeners = new Map();

onSessionChange(() => { cache.clear(); });

const keyOf = (collectionId, cardId) => {
	const c = normalizeId(collectionId);
	const k = normalizeId(cardId);
	return c && k ? `${c}:${k}` : '';
};

function entryFor(key) {
	const hit = cache.get(key);
	if (!hit) return null;
	if (hit.owner === getSessionKey()) return hit;
	cache.delete(key);
	return null;
}

function notify(key, payload) {
	(listeners.get(key) || new Set()).forEach(fn => fn(payload));
}

const shape = data => ({
	mode: ['off', 'chosen', 'every'].includes(data?.mode) ? data.mode : 'off',
	applyWhereReferenced: data?.applyWhereReferenced !== false,
	fromCardId: normalizeId(data?.fromCardId) || null,
	ownMode: data?.ownMode || null,
	count: Number(data?.count) || 0,
	// May this person turn item tags on and off here: manage, and not archived.
	canManage: data?.canManage === true,
	pass: data?.pass || null,
});

function load(key, { force = false } = {}) {
	const hit = entryFor(key);
	if (!force && hit && hit.loaded && Date.now() - hit.at < STALE_MS) return hit.promise;
	if (!force && hit && hit.pending) return hit.promise;
	const [collectionId, cardId] = key.split(':');
	const entry = { at: Date.now(), owner: getSessionKey(), value: hit?.value || null, loaded: Boolean(hit?.loaded), pending: true, promise: null };
	entry.promise = getCardItemTags(collectionId, cardId)
		.then(data => {
			if (entry.owner !== getSessionKey()) {
				if (cache.get(key) === entry) cache.delete(key);
				return { value: null, error: null, foreign: true };
			}
			entry.value = shape(data);
			entry.loaded = true;
			entry.pending = false;
			entry.at = Date.now();
			if (cache.get(key) === entry) notify(key, { value: entry.value, error: null });
			return { value: entry.value, error: null };
		})
		.catch(error => {
			entry.pending = false;
			if (cache.get(key) === entry) {
				cache.delete(key);
				notify(key, { value: entry.value, error });
			}
			return { value: entry.value, error };
		});
	cache.set(key, entry);
	return entry.promise;
}

/**
 * A card's setting changed in `collectionId` (the settings window wrote it):
 * forget what is cached for it — every card of that collection when `cardId`
 * is not given, since an entry on a card covers the cards that extend it —
 * and have the mounted readers ask again.
 */
export function invalidateItemTagSetting(collectionId, cardId = null) {
	const c = normalizeId(collectionId);
	if (!c) return;
	const only = cardId ? keyOf(c, cardId) : null;
	[...cache.keys()].forEach(key => {
		if (only ? key !== only : !key.startsWith(`${c}:`)) return;
		cache.delete(key);
		if (listeners.get(key)?.size) load(key);
	});
}

/** Test seam: forget everything. */
export function resetItemTagSettingCache() {
	cache.clear();
	listeners.clear();
}

const NONE = Object.freeze({ mode: 'off', applyWhereReferenced: true, fromCardId: null, ownMode: null, count: 0, canManage: false, pass: null });

export default function useItemTagSetting(collectionId, cardId, { enabled = true } = {}) {
	const key = enabled ? keyOf(collectionId, cardId) : '';
	const who = useSessionUserId();
	const [state, setState] = useState(() => {
		const hit = key ? entryFor(key) : null;
		return { key, who, value: hit?.value || null, loaded: Boolean(hit?.loaded), error: null };
	});

	useEffect(() => {
		if (!key) return undefined;
		let active = true;
		const put = ({ value, error, foreign }) => {
			if (!active || foreign) return;
			setState(prev => {
				const same = prev.key === key && prev.who === who;
				if (error) return { key, who, value: same ? prev.value : null, loaded: same && prev.loaded, error };
				if (same && prev.value === value && prev.loaded && !prev.error) return prev;
				return { key, who, value, loaded: true, error: null };
			});
		};
		if (!listeners.has(key)) listeners.set(key, new Set());
		listeners.get(key).add(put);
		const hit = entryFor(key);
		setState(prev => (prev.key === key && prev.who === who ? prev : { key, who, value: hit?.value || null, loaded: Boolean(hit?.loaded), error: null }));
		load(key).then(put);
		return () => {
			active = false;
			listeners.get(key)?.delete(put);
		};
	}, [key, who]);

	const refresh = useCallback(() => (key ? load(key, { force: true }) : Promise.resolve({ value: null, error: null })), [key]);

	const current = key && state.key === key && state.who === who;
	const value = (current && state.value) || NONE;
	return {
		...value,
		loaded: Boolean(current && state.loaded),
		error: current ? state.error : null,
		refresh,
	};
}
